import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiLink, FiMoon, FiSun } from 'react-icons/fi';
import { FaXTwitter, FaLinkedin, FaFacebook, FaGithub } from 'react-icons/fa6';
import TerminalBlock from './TerminalBlock';
import BlogArticleShell from './BlogArticleShell';
import { getExploreBlogs } from './blogSidebarData';
import { estimateReadTimeFromSections } from './blogReadTime';

const BLOG_THEME_STORAGE_KEY = 'sr-blog-theme';

const getInitialBlogTheme = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedTheme = window.localStorage.getItem(BLOG_THEME_STORAGE_KEY);
  if (storedTheme === 'light') {
    return false;
  }

  return true;
};

const articleSections = [
  {
    id: 'intro',
    title: 'Self-Hosting a Linux Server with Cloudflare Tunnel',
    paragraphs: [
      'This guide shows how to securely expose a local Linux machine to the internet with Cloudflare Tunnel, without opening ports or configuring router port forwarding.',
    ],
  },
  {
    id: 'what-covered',
    title: 'What This Guide Covers',
    paragraphs: ['This walkthrough is framework-agnostic and works for any service running on localhost.'],
    bullets: [
      'Install and configure Cloudflare Tunnel',
      'Connect a domain to a local Linux machine',
      'Use Nginx as a reverse proxy',
      'Run Cloudflare as a system service',
      'Build a clean, production-style architecture',
      'Works with Django, Flask, FastAPI, Node.js, static sites, and more',
    ],
  },
  {
    id: 'why-tunnel',
    title: 'Why Use Cloudflare Tunnel?',
    paragraphs: [
      'Traditional self-hosting often needs a public IP address, port forwarding, firewall rules, and router configuration.',
      'Cloudflare Tunnel removes that complexity. Your server creates an outbound encrypted connection to Cloudflare, and traffic is proxied securely.',
    ],
    bullets: [
      'No open ports',
      'No public IP required',
      'No router changes',
      'Automatic HTTPS',
      'Encrypted traffic',
      'Zero Trust compatible',
    ],
  },
  {
    id: 'architecture',
    title: 'Architecture Overview',
    diagram: `                Internet
                      |
                      v
              +----------------+
              |   Cloudflare   |
              |  (DNS + HTTPS) |
              +----------------+
                      |
                      v
              +----------------+
              | Cloudflare     |
              | Tunnel         |
              +----------------+
                      |
                      v
              +----------------+
              |  cloudflared   |
              | (Linux Server) |
              +----------------+
                      |
                      v
              +----------------+
              |     Nginx      |
              | Reverse Proxy  |
              +----------------+
                      |
                      v
              +----------------+
              |  Your Local    |
              |  Application   |
              +----------------+`,
  },
  {
    id: 'requirements',
    title: 'Requirements',
    bullets: [
      'Ubuntu or Debian Linux',
      'A domain added to Cloudflare',
      'A local service running on a port (for example localhost:8000)',
    ],
  },
  {
    id: 'step1-install',
    title: 'Step 1 - Install Cloudflared',
    commands: [
      { title: 'install from apt', command: 'sudo apt update\nsudo apt install cloudflared' },
      {
        title: 'install from package when apt is unavailable',
        command:
          'wget https://github.com/cloudflare/cloudflared/releases/latest/download/cloudflared-linux-amd64.deb\nsudo dpkg -i cloudflared-linux-amd64.deb',
      },
      { title: 'verify installation', command: 'cloudflared --version' },
    ],
  },
  {
    id: 'step2-auth',
    title: 'Step 2 - Authenticate with Cloudflare',
    paragraphs: [
      'This command opens your browser, asks you to select your domain, and creates a certificate file at ~/.cloudflared/cert.pem.',
    ],
    commands: [{ title: 'login', command: 'cloudflared tunnel login' }],
  },
  {
    id: 'step3-create',
    title: 'Step 3 - Create a Tunnel',
    paragraphs: ['This generates a tunnel credentials file at ~/.cloudflared/<tunnel-id>.json.'],
    commands: [{ title: 'create tunnel', command: 'cloudflared tunnel create my-tunnel' }],
  },
  {
    id: 'step4-config',
    title: 'Step 4 - Create Tunnel Configuration',
    paragraphs: [
      'Create ~/.cloudflared/config.yml and replace yourdomain.com, <tunnel-id>.json, and the port for your application.',
    ],
    commands: [{ title: 'open config file', command: 'nano ~/.cloudflared/config.yml' }],
    yaml: `tunnel: my-tunnel
credentials-file: /etc/cloudflared/<tunnel-id>.json

ingress:
  - hostname: yourdomain.com
    service: http://localhost:80
  - service: http_status:404`,
  },
  {
    id: 'step5-dns',
    title: 'Step 5 - Route DNS',
    paragraphs: ['This command creates the required DNS record automatically.'],
    commands: [{ title: 'create dns route', command: 'cloudflared tunnel route dns my-tunnel yourdomain.com' }],
  },
  {
    id: 'step6-nginx',
    title: 'Step 6 - Install and Configure Nginx',
    commands: [
      { title: 'install and start nginx', command: 'sudo apt install nginx\nsudo systemctl start nginx' },
      { title: 'edit default server block', command: 'sudo nano /etc/nginx/sites-available/default' },
    ],
    nginx: `server {
    listen 80;
    server_name yourdomain.com;

    location / {
        proxy_pass http://127.0.0.1:8000;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
    }
}`,
    commandsAfter: [{ title: 'validate and restart nginx', command: 'sudo nginx -t\nsudo systemctl restart nginx' }],
  },
  {
    id: 'step7-service',
    title: 'Step 7 - Run Cloudflare Tunnel as a System Service',
    commands: [
      {
        title: 'move config and credentials to /etc/cloudflared',
        command:
          'sudo mkdir -p /etc/cloudflared\nsudo cp ~/.cloudflared/config.yml /etc/cloudflared/\nsudo cp ~/.cloudflared/<tunnel-id>.json /etc/cloudflared/',
      },
      {
        title: 'install and enable service',
        command: 'sudo cloudflared service install\nsudo systemctl enable cloudflared\nsudo systemctl start cloudflared',
      },
      { title: 'check service status', command: 'sudo systemctl status cloudflared' },
    ],
  },
  {
    id: 'testing',
    title: 'Testing',
    paragraphs: [
      'Start any local application (example below on port 8000), then visit https://yourdomain.com. If setup is correct, your local app is now publicly accessible via HTTPS.',
    ],
    commands: [{ title: 'example local server', command: 'python3 -m http.server 8000' }],
  },
  {
    id: 'zero-trust',
    title: 'Optional: Zero Trust Protection',
    paragraphs: ['In Cloudflare Dashboard: Zero Trust -> Access -> Applications.'],
    bullets: ['Restrict by email', 'Require Google login', 'Protect specific paths'],
  },
  {
    id: 'what-you-learn',
    title: 'What You Learn from This Setup',
    bullets: [
      'How reverse proxies work',
      'How encrypted tunnels replace port forwarding',
      'How DNS routing integrates with tunnels',
      'How systemd manages background services',
      'Real-world infrastructure layering',
    ],
    paragraphs: ['This is strong foundational DevOps knowledge for production-minded deployments.'],
  },
  {
    id: 'security',
    title: 'Security Recommendations',
    bullets: [
      'Do not expose sensitive admin panels publicly',
      'Keep your system updated',
      'Use strong authentication',
      'Restrict access with Cloudflare Zero Trust when possible',
    ],
  },
];

function CloudflareTunnelGuidePage() {
  const [isDark, setIsDark] = useState(getInitialBlogTheme);
  const [shareCopyState, setShareCopyState] = useState('idle');
  const exploreBlogs = getExploreBlogs('cloudflare-tunnel-guide');
  const readTime = estimateReadTimeFromSections(articleSections, [
    'A practical deployment guide to expose a local Linux service publicly through Cloudflare Tunnel and Nginx with automatic HTTPS and no open inbound ports.',
  ]);

  const shareUrl = 'https://supanroy.com/blogs/cloudflare-tunnel-guide';
  const shareText = 'Self-Hosting a Linux Server with Cloudflare Tunnel by Supan Roy';

  useEffect(() => {
    document.title = 'Supan Roy | Blogs';
  }, []);

  useEffect(() => {
    window.localStorage.setItem(BLOG_THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  const copyTextWithFallback = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  };

  const handleCopyShareLink = async () => {
    try {
      const copied = await copyTextWithFallback(shareUrl);
      setShareCopyState(copied ? 'success' : 'error');
      setTimeout(() => setShareCopyState('idle'), 1800);
    } catch (error) {
      setShareCopyState('error');
      setTimeout(() => setShareCopyState('idle'), 1800);
    }
  };

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-500 ${
        isDark
          ? 'dark bg-gradient-to-b from-[#0a0f1c] via-[#0f172a] to-[#151f33]'
          : 'bg-gradient-to-b from-slate-50 via-white to-slate-100'
      }`}
    >
      <header className="sticky top-0 z-20 border-b border-slate-300/80 bg-slate-100/95 backdrop-blur dark:border-white/10 dark:bg-[#0a1220]/90 dark:supports-[backdrop-filter]:bg-[#0a1220]/75">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="transition hover:opacity-90" aria-label="Go to home page">
            <img src="/favicon.svg" alt="SR Logo" className="h-8 w-8 rounded-md" />
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/blogs"
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 transition hover:border-slate-400 hover:bg-slate-100 dark:border-white/20 dark:text-slate-100 dark:hover:border-white/40 dark:hover:bg-white/10"
            >
              Blogs
            </Link>
            <button
              type="button"
              onClick={() => setIsDark((prev) => !prev)}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`rounded-full border p-2.5 transition ${
                isDark
                  ? 'border-amber-300/70 bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'border-slate-800/70 bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
            </button>
          </div>
        </div>
      </header>

      <BlogArticleShell sections={articleSections} relatedBlogs={exploreBlogs}>
        <article className="animate-fade-up">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300/90">Technical Blog</p>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            <img
              src="https://cdn.simpleicons.org/cloudflare/F38020"
              alt="Cloudflare logo"
              className="mr-2 inline-block h-10 w-10 align-middle"
            />
            <span className="align-middle">Self-Hosting a Linux Server with Cloudflare Tunnel</span>
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-900 dark:text-slate-100">Supan Roy</span>
            <span>10 Feb, 2026</span>
            <span>{readTime} min read</span>
            <span>11:06</span>
          </div>

          <p className="mt-8 text-lg leading-relaxed text-slate-700 dark:text-slate-200">
            A practical deployment guide to expose a local Linux service publicly through Cloudflare Tunnel and Nginx with automatic HTTPS and no open inbound ports.
          </p>

          {articleSections.map((section, sectionIndex) => (
            <section
              key={section.id}
              id={section.id}
              className="mt-12 scroll-mt-28 animate-fade-up"
              style={{ animationDelay: `${Math.min(sectionIndex * 70, 560)}ms` }}
            >
              <h2 className="mb-4 text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">{section.title}</h2>

              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
                  {paragraph}
                </p>
              ))}

              {section.bullets?.length ? (
                <ul className="mt-5 list-disc space-y-3 pl-6 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
                  {section.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}

              {section.diagram ? (
                <pre className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm leading-relaxed text-slate-700 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200">
                  {section.diagram}
                </pre>
              ) : null}

              {section.commands?.map((codeBlock) => (
                <TerminalBlock key={`${section.id}-${codeBlock.title}`} title={codeBlock.title} command={codeBlock.command} />
              ))}

              {section.yaml ? (
                <pre className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm leading-relaxed text-slate-700 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200">
                  {section.yaml}
                </pre>
              ) : null}

              {section.nginx ? (
                <pre className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm leading-relaxed text-slate-700 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200">
                  {section.nginx}
                </pre>
              ) : null}

              {section.commandsAfter?.map((codeBlock) => (
                <TerminalBlock key={`${section.id}-${codeBlock.title}`} title={codeBlock.title} command={codeBlock.command} />
              ))}
            </section>
          ))}

          <section className="mt-14 scroll-mt-28">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Share this blog</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              If this guide helped, share it with your network.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
              <a
                href={`https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaXTwitter size={15} />
                <span>Share on X</span>
              </a>

              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaLinkedin size={15} />
                <span>Share on LinkedIn</span>
              </a>

              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 no-underline transition hover:border-slate-400 hover:bg-slate-200 hover:no-underline dark:border-white/20 dark:bg-white/[0.04] dark:text-slate-200 dark:hover:border-white/40 dark:hover:bg-white/[0.1] dark:hover:text-white"
              >
                <FaFacebook size={15} />
                <span>Share on Facebook</span>
              </a>

              <button
                type="button"
                onClick={handleCopyShareLink}
                className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-900 dark:border-slate-300 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              >
                <FiLink size={15} />
                <span>
                  {shareCopyState === 'success' ? 'Copied!' : shareCopyState === 'error' ? 'Copy failed' : 'Copy link'}
                </span>
              </button>
            </div>
          </section>

          <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-[#0f172a]/80">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Connect with me</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              Follow for more technical updates and projects.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <a
                href="https://x.com/supanroy0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black text-white">
                  <FaXTwitter size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">X</span>
              </a>

              <a
                href="https://linkedin.com/in/supanroy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0A66C2] text-white">
                  <FaLinkedin size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">LinkedIn</span>
              </a>

              <a
                href="https://facebook.com/supan.being.roy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#1877F2] text-white">
                  <FaFacebook size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">Facebook</span>
              </a>

              <a
                href="https://github.com/Supan-Roy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 no-underline transition hover:border-slate-400 hover:bg-slate-100 hover:no-underline dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/30 dark:hover:bg-white/[0.06]"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#111827] text-white">
                  <FaGithub size={16} />
                </span>
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">GitHub</span>
              </a>
            </div>
          </section>
        </article>
      </BlogArticleShell>
    </div>
  );
}

export default CloudflareTunnelGuidePage;